import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  LifeBuoy,
  LoaderCircle,
  LockKeyhole,
  MessageSquareText,
  Plus,
  RefreshCw,
  Send,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/AuthContext";
import {
  createSupportTicket,
  getSupportTicket,
  listSupportTickets,
  replySupportTicket,
} from "@/features/support/api";
import {
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_PRIORITY_LABELS,
  SUPPORT_STATUS_LABELS,
  type SupportMessage,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
} from "@/features/support/types";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { trackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/productos/tickets")({
  head: () => ({
    meta: [
      { title: "Soporte privado | Socrattica" },
      {
        name: "description",
        content: "Crea y consulta solicitudes privadas de soporte en tu cuenta de Socrattica.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: TicketsPage,
});

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function TicketsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [view, setView] = useState<"new" | "history">("new");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [category, setCategory] = useState<SupportTicketCategory>("purchase");
  const [priority, setPriority] = useState<SupportTicketPriority>("normal");

  const loadTickets = useCallback(async () => {
    if (!user) return;
    setTicketsLoading(true);
    setTicketsError("");
    try {
      setTickets(await listSupportTickets(user));
    } catch (error) {
      setTicketsError(error instanceof Error ? error.message : "No pudimos consultar tus tickets.");
    } finally {
      setTicketsLoading(false);
    }
  }, [user]);

  const openTicket = useCallback(
    async (ticketId: string) => {
      if (!user) return;
      setDetailLoading(true);
      try {
        const detail = await getSupportTicket(user, ticketId);
        setSelectedTicket(detail.ticket);
        setMessages(detail.messages);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "No pudimos abrir el ticket.");
      } finally {
        setDetailLoading(false);
      }
    },
    [user],
  );

  useEffect(() => {
    if (user) void loadTickets();
  }, [loadTickets, user]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    const form = event.currentTarget;
    const formData = new FormData(form);
    setSubmitting(true);

    try {
      const ticket = await createSupportTicket(user, {
        category,
        priority,
        subject: String(formData.get("subject") ?? ""),
        message: String(formData.get("message") ?? ""),
      });
      form.reset();
      setCategory("purchase");
      setPriority("normal");
      setTickets((current) => [ticket, ...current]);
      setView("history");
      await openTicket(ticket.id);
      void trackEvent("support_ticket_create", {
        category,
        priority,
      });
      toast.success(`Ticket ${ticket.reference} creado correctamente.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos crear el ticket.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !selectedTicket) return;
    const form = event.currentTarget;
    const formData = new FormData(form);
    const body = String(formData.get("reply") ?? "");
    setReplying(true);

    try {
      const message = await replySupportTicket(user, selectedTicket.id, body);
      setMessages((current) => [...current, message]);
      setSelectedTicket((current) => (current ? { ...current, status: "open" } : current));
      form.reset();
      await loadTickets();
      void trackEvent("support_ticket_reply", { ticket_reference: selectedTicket.reference });
      toast.success("Tu respuesta fue enviada.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos enviar la respuesta.");
    } finally {
      setReplying(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <PageIntro
          compact
          eyebrow="Centro de ayuda"
          title="Soporte privado."
          description="Crea una solicitud, conserva el historial y recibe la respuesta del equipo en tu cuenta."
          details={["Privado", "Trazable", "En tu cuenta"]}
        />

        <section className="py-10 sm:py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            {authLoading ? (
              <div className="flex min-h-64 items-center justify-center" role="status">
                <LoaderCircle className="h-6 w-6 animate-spin" />
                <span className="sr-only">Verificando tu cuenta</span>
              </div>
            ) : !user ? (
              <SignInRequired />
            ) : (
              <>
                <div
                  className="grid max-w-xl grid-cols-2 rounded-lg border border-black/15 bg-white p-1"
                  role="tablist"
                  aria-label="Opciones de soporte"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "new"}
                    onClick={() => setView("new")}
                    className={`inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md text-xs font-semibold transition ${
                      view === "new" ? "bg-black text-white" : "text-black/65 hover:bg-black/5"
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                    Crear ticket
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === "history"}
                    onClick={() => setView("history")}
                    className={`inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md text-xs font-semibold transition ${
                      view === "history" ? "bg-black text-white" : "text-black/65 hover:bg-black/5"
                    }`}
                  >
                    <MessageSquareText className="h-4 w-4" />
                    Mis tickets {tickets.length > 0 ? `(${tickets.length})` : ""}
                  </button>
                </div>

                {view === "new" ? (
                  <NewTicketForm
                    email={user.email ?? ""}
                    category={category}
                    priority={priority}
                    submitting={submitting}
                    onCategoryChange={setCategory}
                    onPriorityChange={setPriority}
                    onSubmit={handleCreate}
                  />
                ) : (
                  <TicketHistory
                    tickets={tickets}
                    ticketsLoading={ticketsLoading}
                    ticketsError={ticketsError}
                    selectedTicket={selectedTicket}
                    messages={messages}
                    detailLoading={detailLoading}
                    replying={replying}
                    onRefresh={() => void loadTickets()}
                    onOpen={(ticketId) => void openTicket(ticketId)}
                    onReply={handleReply}
                  />
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function SignInRequired() {
  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-black/15 p-7 text-center sm:p-10">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-black text-white">
        <LockKeyhole className="h-5 w-5" />
      </span>
      <h2 className="mt-5 text-2xl font-semibold tracking-[-0.04em]">
        Inicia sesión para contactar soporte
      </h2>
      <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-black/60">
        Tus solicitudes y las respuestas del equipo contienen información privada. Por eso se
        muestran únicamente dentro de tu cuenta.
      </p>
      <Button asChild className="mt-6">
        <Link to="/acceso">
          Ingresar o crear cuenta
          <ArrowRight />
        </Link>
      </Button>
    </div>
  );
}

function NewTicketForm({
  email,
  category,
  priority,
  submitting,
  onCategoryChange,
  onPriorityChange,
  onSubmit,
}: {
  email: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  submitting: boolean;
  onCategoryChange: (value: SupportTicketCategory) => void;
  onPriorityChange: (value: SupportTicketPriority) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <form onSubmit={onSubmit} className="space-y-5 rounded-xl border border-black/15 p-6 sm:p-8">
        <div className="border-b border-black/10 pb-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
            Nueva solicitud
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-[-0.04em]">
            Cuéntanos qué necesitas
          </h2>
        </div>

        <div>
          <Label htmlFor="support-email">Correo de la cuenta</Label>
          <Input id="support-email" value={email} readOnly className="mt-2 bg-black/[0.03]" />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label id="support-category-label">Categoría</Label>
            <Select
              value={category}
              onValueChange={(value) => onCategoryChange(value as SupportTicketCategory)}
            >
              <SelectTrigger className="mt-2 h-11" aria-labelledby="support-category-label">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUPPORT_CATEGORY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label id="support-priority-label">Prioridad</Label>
            <Select
              value={priority}
              onValueChange={(value) => onPriorityChange(value as SupportTicketPriority)}
            >
              <SelectTrigger className="mt-2 h-11" aria-labelledby="support-priority-label">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUPPORT_PRIORITY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="support-subject">Asunto</Label>
          <Input
            id="support-subject"
            name="subject"
            className="mt-2"
            minLength={5}
            maxLength={120}
            placeholder="Ej. No puedo descargar mi guía"
            required
          />
        </div>

        <div>
          <Label htmlFor="support-message">Mensaje</Label>
          <Textarea
            id="support-message"
            name="message"
            className="mt-2 min-h-40 resize-y"
            minLength={10}
            maxLength={3000}
            placeholder="Incluye el número del pedido o el nombre del recurso y describe lo que ocurrió."
            required
          />
        </div>

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? <LoaderCircle className="animate-spin" /> : <Send />}
          {submitting ? "Creando ticket…" : "Enviar solicitud"}
        </Button>
      </form>

      <aside className="space-y-4">
        <div className="rounded-xl border border-black bg-black p-6 text-white">
          <LifeBuoy className="h-5 w-5" />
          <h2 className="mt-4 text-xl font-semibold">Una conversación con historial</h2>
          <p className="mt-2 text-sm leading-6 text-white/70">
            Cada respuesta queda guardada en el mismo ticket para que no tengas que repetir el
            contexto.
          </p>
        </div>
        <div className="rounded-xl border border-black/15 p-6">
          <Clock3 className="h-5 w-5" />
          <h2 className="mt-4 font-semibold">Ayúdanos a responder mejor</h2>
          <p className="mt-2 text-sm leading-6 text-black/60">
            Incluye el correo de compra, el recurso y el mensaje exacto que aparece en pantalla.
          </p>
        </div>
      </aside>
    </div>
  );
}

function TicketHistory({
  tickets,
  ticketsLoading,
  ticketsError,
  selectedTicket,
  messages,
  detailLoading,
  replying,
  onRefresh,
  onOpen,
  onReply,
}: {
  tickets: SupportTicket[];
  ticketsLoading: boolean;
  ticketsError: string;
  selectedTicket: SupportTicket | null;
  messages: SupportMessage[];
  detailLoading: boolean;
  replying: boolean;
  onRefresh: () => void;
  onOpen: (ticketId: string) => void;
  onReply: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <div className="mt-6 grid min-h-[560px] overflow-hidden rounded-xl border border-black/15 lg:grid-cols-[340px_minmax(0,1fr)]">
      <section className="border-b border-black/10 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between border-b border-black/10 p-5">
          <div>
            <h2 className="font-semibold">Solicitudes</h2>
            <p className="mt-1 text-xs text-black/60">Solo visibles en tu cuenta.</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            disabled={ticketsLoading}
            aria-label="Actualizar tickets"
          >
            <RefreshCw className={ticketsLoading ? "animate-spin" : ""} />
          </Button>
        </div>

        {ticketsLoading ? (
          <div className="flex items-center gap-3 p-6 text-sm text-black/65" role="status">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            Consultando…
          </div>
        ) : ticketsError ? (
          <div className="p-6">
            <p className="text-sm font-medium">No pudimos cargar tus tickets</p>
            <p className="mt-2 text-xs leading-5 text-black/60">{ticketsError}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}>
              Reintentar
            </Button>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-7 text-center">
            <MessageSquareText className="mx-auto h-6 w-6" />
            <p className="mt-3 font-semibold">Aún no tienes tickets</p>
            <p className="mt-2 text-xs leading-5 text-black/60">
              Crea una solicitud cuando necesites ayuda.
            </p>
          </div>
        ) : (
          <div className="max-h-[600px] divide-y divide-black/10 overflow-y-auto">
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => onOpen(ticket.id)}
                className={`block w-full cursor-pointer p-5 text-left transition hover:bg-black/[0.03] ${
                  selectedTicket?.id === ticket.id ? "bg-black/[0.05]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs font-semibold">{ticket.reference}</span>
                  <TicketStatus status={ticket.status} />
                </div>
                <p className="mt-3 line-clamp-2 text-sm font-semibold">{ticket.subject}</p>
                <p className="mt-2 line-clamp-2 text-xs leading-5 text-black/60">
                  {ticket.lastMessagePreview}
                </p>
                <p className="mt-3 text-xs text-black/60">{formatDate(ticket.updatedAt)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="min-w-0">
        {detailLoading ? (
          <div className="flex min-h-[420px] items-center justify-center" role="status">
            <LoaderCircle className="h-5 w-5 animate-spin" />
            <span className="sr-only">Cargando conversación</span>
          </div>
        ) : !selectedTicket ? (
          <div className="flex min-h-[420px] items-center justify-center p-8 text-center">
            <div>
              <MessageSquareText className="mx-auto h-7 w-7" />
              <h2 className="mt-4 text-xl font-semibold">Selecciona un ticket</h2>
              <p className="mt-2 text-sm text-black/60">
                Aquí verás la conversación y la respuesta del equipo.
              </p>
            </div>
          </div>
        ) : (
          <>
            <header className="border-b border-black/10 p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <TicketStatus status={selectedTicket.status} />
                <span className="font-mono text-xs text-black/60">{selectedTicket.reference}</span>
              </div>
              <h2 className="mt-3 text-xl font-semibold tracking-[-0.03em]">
                {selectedTicket.subject}
              </h2>
              <p className="mt-2 text-xs text-black/60">
                {SUPPORT_CATEGORY_LABELS[selectedTicket.category]} · Prioridad{" "}
                {SUPPORT_PRIORITY_LABELS[selectedTicket.priority].toLowerCase()}
              </p>
            </header>

            <div
              className="max-h-[420px] space-y-4 overflow-y-auto bg-black/[0.02] p-5 sm:p-6"
              aria-live="polite"
            >
              {messages.map((message) => (
                <article
                  key={message.id}
                  className={`max-w-[88%] rounded-xl border p-4 ${
                    message.senderRole === "admin"
                      ? "mr-auto border-black bg-black text-white"
                      : "ml-auto border-black/15 bg-white"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs font-semibold">{message.senderName}</p>
                    <p
                      className={`text-xs ${
                        message.senderRole === "admin" ? "text-white/70" : "text-black/55"
                      }`}
                    >
                      {formatDate(message.createdAt)}
                    </p>
                  </div>
                  <p
                    className={`mt-3 whitespace-pre-wrap text-sm leading-6 ${
                      message.senderRole === "admin" ? "text-white/85" : "text-black/75"
                    }`}
                  >
                    {message.body}
                  </p>
                </article>
              ))}
            </div>

            {selectedTicket.status === "closed" ? (
              <div className="flex items-center gap-3 border-t border-black/10 p-5 text-sm">
                <CheckCircle2 className="h-5 w-5" />
                Este ticket fue cerrado. Crea uno nuevo si necesitas más ayuda.
              </div>
            ) : (
              <form onSubmit={onReply} className="border-t border-black/10 p-5 sm:p-6">
                <Label htmlFor="support-reply">Añadir respuesta</Label>
                <Textarea
                  id="support-reply"
                  name="reply"
                  className="mt-2 min-h-24 resize-y"
                  minLength={2}
                  maxLength={3000}
                  required
                />
                <Button type="submit" className="mt-3" disabled={replying}>
                  {replying ? <LoaderCircle className="animate-spin" /> : <Send />}
                  {replying ? "Enviando…" : "Responder"}
                </Button>
              </form>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function TicketStatus({ status }: { status: SupportTicket["status"] }) {
  const classes = {
    open: "bg-amber-100 text-amber-950",
    answered: "bg-emerald-100 text-emerald-950",
    closed: "bg-black/10 text-black/70",
  };

  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${classes[status]}`}>
      {SUPPORT_STATUS_LABELS[status]}
    </span>
  );
}
